import { FC, PropsWithChildren, ReactNode } from 'react'
import { Box, Breakpoint, colors, IconButton, Paper, styled, Tooltip, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import { mapGlobalModalContext } from '../api-context'
import type { BoxProps, IconButtonProps, PaperProps } from '@mui/material'

export interface IFormModalWrapperProps extends PropsWithChildren {
  title: ReactNode
  size?: Breakpoint
  fullHeight?: boolean
  onCloseClick?: IconButtonProps['onClick']
  onBackClick?: IconButtonProps['onClick']
  enableCloseButton?: boolean
  enableBackButton?: boolean
  slots?: {
    wrapProps?: Partial<PaperProps>
    topBarProps?: BoxProps
    beforeTitle?: JSX.Element
    iconSize?: 'small' | 'medium' | 'large'
    iconBackProps?: Partial<IconButtonProps>
    iconCloseProps?: Partial<IconButtonProps>
  }
}

const FormModalWrapper: FC<IFormModalWrapperProps> = (props) => {
  const renderTitle = () => {
    const { title } = props
    if (typeof title === 'string') {
      return (
        <Typography component='h3' variant='subtitle1' sx={{ mt: '2px', ml: '8px', fontWeight: 700 }}>
          {title}
        </Typography>
      )
    }
    return title
  }

  return mapGlobalModalContext((context) => {
    const rootClasses = [modalWrapperClasses.root, props.slots?.wrapProps?.className]
    if (props.fullHeight) rootClasses.push(modalWrapperClasses.fullHeight)

    const topBarClasses = [modalWrapperClasses.topBar, props.slots?.topBarProps?.className].filter(Boolean).join(' ')
    return (
      <ModalWrapperStyled size={props.size} {...props.slots?.wrapProps} className={rootClasses.filter(Boolean).join(' ')}>
        <TopBar {...props.slots?.topBarProps} className={topBarClasses}>
          {Boolean(props.enableBackButton) && (
            <Tooltip title='Back' placement='top' arrow>
              <BtnBack onClick={props.onBackClick} {...props.slots?.iconBackProps}>
                <ChevronLeftIcon fontSize={props.slots?.iconSize ?? 'medium'} />
              </BtnBack>
            </Tooltip>
          )}
          {props.slots?.beforeTitle && (
            <>
              {props.slots.beforeTitle}
              <Box flex={1} />
            </>
          )}
          {renderTitle()}
          <Box flex={1} />
          {props.enableCloseButton !== false && (
            <Tooltip title='Close' placement='top' arrow>
              <BtnClose
                onClick={(e) => {
                  props.onCloseClick && props.onCloseClick(e)
                  context.close()
                }}
                {...props.slots?.iconCloseProps}
              >
                <CloseIcon fontSize={props.slots?.iconSize ?? 'medium'} />
              </BtnClose>
            </Tooltip>
          )}
        </TopBar>
        <div className={modalWrapperClasses.content}>{props.children}</div>
      </ModalWrapperStyled>
    )
  })
}
export default FormModalWrapper

const modalWrapperClasses = {
  root: 'Dino-ModalWrapper-root',
  topBar: 'Dino-ModalWrapper-topBar',
  content: 'Dino-ModalWrapper-content',
  fullHeight: 'Dino-ModalWrapper-fullHeight'
}

const ModalWrapperStyled = styled(Paper)<{ size?: Breakpoint }>(({ theme, size }) => ({
  maxHeight: 'calc(100vh - 24px)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  maxWidth: 'calc(100vw - 24px)',
  width: theme.breakpoints.values[size ?? 'lg'],
  [theme.breakpoints.down('sm')]: { width: 'calc(100vw - 24px)' },
  [`&.${modalWrapperClasses.fullHeight}`]: { height: 'calc(100vh - 24px)' },
  [`.${modalWrapperClasses.content}`]: {
    overflowY: 'auto',
    flex: '1 1 auto',
    minHeight: 0
  }
}))

const BtnClose = styled(IconButton)({
  flex: '0 0 auto',
  color: '#3c3c3c',
  '& svg': { transition: 'all 0.2s' },
  '&:hover svg': { color: colors.red[600], transform: 'rotate(90deg)' }
})

const BtnBack = styled(IconButton)({
  flex: '0 0 auto',
  color: '#3c3c3c',
  '& svg': { transition: 'all 0.2s' },
  '&:hover svg': { color: colors.blue[600], transform: 'scale(1.2)' }
})

const TopBar = styled(Box)(({ theme }) => ({
  height: 'var(--dino-h-top-bar, 48px)',
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  flex: '0 0 auto',
  boxShadow: 'rgba(145, 158, 171, 0.1) 0px 0px 2px 0px,rgba(145, 158, 171, 0.12) 0px 0px 24px -4px'
}))
